// utils/intentDetection.ts
// Simple utilities for unified text box input validation

import { parseChordProgression } from './chordParser';

// ================================================================================
// TYPES AND INTERFACES
// ================================================================================

export interface ChordValidationResult {
  readonly isValidChords: boolean;
  readonly validationError: string | null;
}

// ================================================================================
// VALIDATION FUNCTIONS
// ================================================================================

/**
 * Check if input looks like a chord progression
 * @param input - Raw user input
 * @returns True if it looks like chords
 */
export function looksLikeChords(input: string): boolean {
  if (!input || input.trim() === '') {
    return false;
  }

  const result = parseChordProgression(input.trim());
  return result.chords.length > 0 && result.errors.length === 0;
}

/**
 * Get validation errors for chord input
 * @param input - Chord progression input
 * @returns Error message or null if valid
 */
export function getChordValidationError(input: string): string | null {
  if (!input || input.trim() === '') {
    return null;
  }

  const result = parseChordProgression(input.trim());
  if (result.errors.length > 0) {
    return result.errors[0];
  }
  
  return null;
}

/**
 * Comprehensive validation for chord input
 * @param input - Chord progression input
 * @returns Validation result with both validity and error message
 */
export function validateChordInput(input: string): ChordValidationResult {
  const isValidChords = looksLikeChords(input);
  const validationError = getChordValidationError(input);
  
  return {
    isValidChords,
    validationError
  };
}
